import { Component, OnInit } from '@angular/core';
import { NgProgress } from '@ngx-progressbar/core';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { ToastrService } from 'ngx-toastr';
import { APIOperableComponent } from '../../../../shared/components/api-operable/api-operable.component';
import { EditableComponent } from '../../../../shared/components/interfaces/editable.component';
import { ListeableComponent } from '../../../../shared/components/interfaces/listeable.component';
import { UIDialogService } from '../../../../shared/services/ui-dialog.service';
import { MiscellaneousUtils } from '../../../../shared/utils/miscellaneous.utils';
import { SortUtils } from '../../../../shared/utils/sort.utils';
import { AuthService } from '../../../security/service/auth.service';
import { PartCategoryUIMessages } from '../../model/part-category-ui.messages';
import { PartCategoryModel } from '../../model/part-category.model';
import { PartCategoryService } from '../../service/part-category.service';
import { PartCategoryAddDialogComponent } from '../part-category-add-dialog/part-category-add-dialog.component';

@Component({
  selector: 'part-category-list',
  templateUrl:
    'console/app-js/modules/partCategory/components/part-category-list/part-category-list.component.html'
})
export class PartCategoryListComponent extends APIOperableComponent
  implements ListeableComponent, EditableComponent, OnInit {
  protected partCategoryData: Array<PartCategoryModel>;
  protected data: Array<PartCategoryModel> = [];
  protected originalData: Array<PartCategoryModel> = [];
  protected allPartCategory: Array<PartCategoryModel> = [];
  protected gridView: GridDataResult;
  protected pageSize = 100;
  protected skip = 0;
  protected multiple = false;
  protected allowUnsort = true;
  protected sort: SortDescriptor[] = [
    {
      field: 'name',
      dir: 'asc'
    }
  ];

  protected partCategory: Array<string> = [
    'Active and Inactive',
    'Active',
    'Inactive'
  ];
  protected partCategoryStatus: Array<string>;
  protected loading = false;

  protected partCategoryInputData: string;
  protected partCategoryStatusInputData: boolean;
  protected customInputType: any;

  constructor(
    protected progressService: NgProgress,
    protected toastr: ToastrService,
    protected authService: AuthService,
    protected dialogService: UIDialogService,
    protected partCategoryService: PartCategoryService
  ) {
    super(progressService, toastr, authService);

    this.onSearch();
  }

  ngOnInit() {
    this.partCategoryStatus = this.partCategory.slice();
  }

  /**
   * Load all part categories.
   */
  onSearch(): void {
    this.startProgress();
    this.loading = true;
    this.partCategoryService.getPartCategories().subscribe(
      result => {
        this.data = result;
        this.originalData = MiscellaneousUtils.cloneArray(this.data);
        this.partCategoryData = this.originalData.map(partCategory => {
          return {
            id: partCategory.id,
            name: partCategory.name,
            isActive: partCategory.isActive,
            description: partCategory.description
          };
        });
        this.allPartCategory = this.partCategoryData.sort((a, b) =>
          SortUtils.compareByProperty(a, b, 'name')
        );
        this.partCategoryData = this.allPartCategory;
        this.loadProducts();
        this.finishProgress();
        this.loading = false;
      },
      err =>
        this.onAPIError(PartCategoryUIMessages.LOAD_Part_Category_LIST_ERROR)
    );
    this.customInputType = this.partCategory[0];
  }

  /**
   * Filter data.
   */
  protected handleFilter(value) {
    if (value.length >= 1) {
      this.partCategoryStatus = this.partCategory.filter(
        s => s.toLowerCase().indexOf(value.toLowerCase()) !== -1
      );
    } else {
      this.partCategoryStatus = this.partCategory.slice();
    }
  }

  /**
   * Reload all part categories after added new part category.
   */
  onSave(): void {
    // Silence is golden.
  }

  onCancel(param: any): void {
    // Silence is golden.
  }

  /**
   * Dirty check of data.
   */
  isDirty(): boolean {
    return false;
  }

  /**
   * Filter data on selection of active or inactive.
   */
  protected selectionChange(value: any): void {
    // filter our data
    let temp: any;
    if (value && value !== 'Active and Inactive') {
      value = value === 'Active' ? true : false;
      this.partCategoryStatusInputData = value;
      temp = this.allPartCategory.filter(row => {
        return (
          row.isActive === value &&
          (this.partCategoryInputData !== undefined
            ? row.name
                .toLowerCase()
                .indexOf(this.partCategoryInputData.toLowerCase()) !== -1 ||
              !this.partCategoryInputData
            : true)
        );
      });
    } else {
      this.partCategoryInputData = undefined;
      temp = this.allPartCategory.filter(row => {
        return this.partCategoryInputData !== undefined
          ? row.name
              .toLowerCase()
              .indexOf(this.partCategoryInputData.toLowerCase()) !== -1 ||
              !this.partCategoryInputData
          : true;
      });
    }
    this.partCategoryData = temp;
    this.skip = 0;
    this.loadProducts();
  }

  /**
   * On Filter input text data.
   */
  protected onFilterData(partCategory: any): void {
    let value = partCategory.target.value.toLocaleLowerCase();
    let temp: any;
    // filter our data
    if (this.partCategoryStatusInputData !== undefined) {
      temp = this.allPartCategory.filter(row => {
        return (
          (row.name.toLowerCase().indexOf(value) !== -1 || !value) &&
          row.isActive === this.partCategoryStatusInputData
        );
      });
    } else {
      temp = this.allPartCategory.filter(row => {
        return row.name.toLowerCase().indexOf(value) !== -1 || !value;
      });
    }
    this.partCategoryData = temp;
    this.skip = 0;
    this.loadProducts();
  }

  /**
   * Load data for page change.
   */
  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.loadProducts();
  }

  /**
   * Load data on grid view.
   */
  private loadProducts(): void {
    this.gridView = {
      data: this.partCategoryData.slice(this.skip, this.skip + this.pageSize),
      total: this.partCategoryData.length
    };
  }

  /**
   * Sort data on sort change.
   */
  protected sortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.sortProducts();
  }

  /**
   * Show sorted data on grid view.
   */
  private sortProducts(): void {
    this.gridView = {
      data: orderBy(this.partCategoryData, this.sort),
      total: this.partCategoryData.length
    };
  }

  /**
   * Open dialog box to add new part category.
   */
  onAddItem(): void {
    this.dialogService
      .open(PartCategoryAddDialogComponent, [
        { provide: 'partCategoryModel', useValue: null }
      ])
      .then(result => {
        if (result) {
          this.onSearch();
          this.onReset();
        }
      })
      .catch(result => {
        // Silence is golden.
      });
  }

  /**
   * onChange switch value.
   */
  valueChanged(dataItem): void {
    if (dataItem.isActive) {
      dataItem.isActive = false;
      this.onDeleteItem(dataItem);
    } else {
      dataItem.isActive = true;
      this.onEditItem(dataItem);
    }
  }

  /**
   * Change inactive item to active.
   */
  onEditItem(partCategoryModel: PartCategoryModel): void {
    this.startProgress();
    this.partCategoryService.updatePartCategory(partCategoryModel).subscribe(
      result => {
        if (result.responseCode === 200) {
          this.onAPISuccess(PartCategoryUIMessages.EDIT_Part_Category_SUCCESS);
          partCategoryModel.isActive = true;
          this.selectionChange(this.customInputType);
          this.finishProgress();
        } else {
          partCategoryModel.isActive = false;
          this.onAPIError(PartCategoryUIMessages.EDIT_Part_Category_ERROR);
        }
      },
      err => {
        partCategoryModel.isActive = false;
        this.onAPIError(PartCategoryUIMessages.EDIT_Part_Category_ERROR);
      }
    );
  }

  /**
   * Change active item to inactive.
   */
  onDeleteItem(partCategoryModel: PartCategoryModel): void {
    this.startProgress();
    this.partCategoryService.updatePartCategory(partCategoryModel).subscribe(
      result => {
        if (result.responseCode === 200) {
          this.onAPISuccess(PartCategoryUIMessages.EDIT_Part_Category_SUCCESS);
          partCategoryModel.isActive = false;
          this.selectionChange(this.customInputType);
          this.finishProgress();
        } else {
          partCategoryModel.isActive = true;
          this.onAPIError(PartCategoryUIMessages.EDIT_Part_Category_ERROR);
        }
      },
      err => {
        partCategoryModel.isActive = true;
        this.onAPIError(PartCategoryUIMessages.EDIT_Part_Category_ERROR);
      }
    );
  }

  onDeleteItems(): void {
    // Silence is golden.
  }

  onReset(): void {
    this.partCategoryInputData = undefined;
    this.customInputType = this.partCategory[0];
    this.selectionChange(this.partCategory[0]);
  }

  onFullscreenMode(): void {
    // Silence is golden.
  }
}
